import { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { CheckCircle2, Loader2, Clock } from 'lucide-react';
import Seo from '@/components/Seo';

export default function BillingSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const { refresh } = useAuth();
  const stopRef = useRef(false);

  useEffect(() => {
    stopRef.current = false;

    const finish = async () => {
      try { if (refresh) await refresh(); } catch (_) {}
      setStatus('success');
    };

    const trySync = async () => {
      // Safety-net: ask backend to pull latest subscription from Stripe directly
      try {
        const { data } = await api.post('/billing/sync');
        if (data && data.is_pro) {
          await finish();
          return true;
        }
      } catch (_) {}
      return false;
    };

    const poll = async (n = 0) => {
      if (stopRef.current) return;
      setAttempts(n);
      // 12 polls @ 1.5s = ~18s before falling back to manual sync
      if (n > 11) {
        const synced = await trySync();
        if (!synced) setStatus('pending');
        return;
      }
      try {
        if (sessionId) {
          const { data } = await api.get(`/billing/checkout/${sessionId}`);
          if (data.status === 'completed') {
            // Double-tap sync for fast tier propagation, then finish
            await trySync();
            await finish();
            return;
          }
          if (data.status === 'expired') {
            // even on "expired" session, see if a sub was created on the customer
            const synced = await trySync();
            if (!synced) setStatus('pending');
            return;
          }
        } else {
          // No session id — go straight to manual sync
          const synced = await trySync();
          if (synced) return;
        }
      } catch (_) {
        // ignore and retry — final attempt will fall back to sync
      }
      setTimeout(() => poll(n + 1), 1500);
    };

    poll();
    return () => { stopRef.current = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-w-2xl mx-auto px-4 py-20" data-testid="billing-success-page">
      <Seo title="Subscription" noindex />
      <div className="tm-card p-8 text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="animate-spin mx-auto text-slate-900" size={48}/>
            <h1 className="font-display font-semibold text-3xl uppercase tracking-tight mt-4">Confirming your subscription…</h1>
            <p className="text-slate-600 mt-2">Talking to Stripe (attempt {attempts + 1}/12)</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle2 className="mx-auto text-brand" size={64}/>
            <h1 data-testid="billing-success-title" className="font-display font-semibold text-3xl sm:text-4xl uppercase tracking-tight mt-4">You&apos;re in!</h1>
            <p className="text-slate-600 mt-2">Your subscription is active. We&apos;ve unlocked your premium features — AI Estimator, unlimited quotes, branded PDFs and more.</p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <Link to="/dashboard" data-testid="success-go-dashboard" className="tm-btn">Go to dashboard</Link>
              <Link to="/assistant" className="tm-btn tm-btn-dark">Try AI Assistant</Link>
            </div>
          </>
        )}
        {status === 'pending' && (
          <>
            <Clock className="mx-auto text-brand" size={64}/>
            <h1 data-testid="billing-pending-title" className="font-display font-semibold text-3xl sm:text-4xl uppercase tracking-tight mt-4">Payment received</h1>
            <p className="text-slate-600 mt-2">
              Stripe is taking a moment to confirm your subscription. Your premium features will appear automatically within a minute or two — no action needed.
            </p>
            <p className="text-slate-500 text-sm mt-3">
              If you still don&apos;t see your badge after 2 minutes, tap the button below.
            </p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <button
                data-testid="billing-retry-sync"
                onClick={async () => {
                  setStatus('checking');
                  try {
                    const { data } = await api.post('/billing/sync');
                    if (data && data.is_pro) {
                      if (refresh) await refresh();
                      setStatus('success');
                      return;
                    }
                  } catch (_) {}
                  setStatus('pending');
                }}
                className="tm-btn"
              >
                Refresh status
              </button>
              <Link to="/dashboard" className="tm-btn tm-btn-dark">Go to dashboard</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
